import React, { useState, useRef } from 'react';
import { Button, Card, Typography, Space, message } from 'antd';
import { AudioOutlined, StopOutlined } from '@ant-design/icons';
import 'antd/dist/reset.css';
import { API_BASE_URL } from '../config/apiConfig';
import { useTranslation } from 'react-i18next';
import HomeButton from '../components/HomeButton';

const { Title } = Typography;

function encodeWAV(audioBuffer: AudioBuffer): Blob {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const samples = audioBuffer.getChannelData(0); // mono

  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([view], { type: 'audio/wav' });
}

export const SpeechToTextPage = () => {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcription, setTranscription] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const { t } = useTranslation();
  const token = localStorage.getItem('token');

  const startRecording = () => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        audioChunksRef.current = [];

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            audioChunksRef.current.push(e.data);
          }
        };

        mediaRecorder.onstop = handleRecordingStop;
        mediaRecorder.start(1000);

        setRecording(true);
        message.success('Recording started');
      })
      .catch((err) => {
        console.error('Error accessing microphone', err);
        message.error('Error accessing microphone');
      });
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.requestData();
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
    message.success('Recording stopped');
  };

  const handleRecordingStop = async () => {
    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioContext = new AudioContext();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      const wavBlob = encodeWAV(audioBuffer);
      const wavUrl = URL.createObjectURL(wavBlob);
      setAudioUrl(wavUrl);

      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result?.toString().split(',')[1];
        if (base64String) {
          sendAudioToServer(base64String);
        }
      };
      reader.readAsDataURL(wavBlob);
    } catch (error) {
      console.error('Error converting to WAV', error);
      message.error('Failed to process audio');
    }

    audioChunksRef.current = [];
  };

  const sendAudioToServer = (base64Audio: string) => {
    const headers: HeadersInit = {
      'Content-Type': 'text/plain',
      'ngrok-skip-browser-warning': 'true',
    };
    if (token) {
      headers['Authorization'] = token;
    }

    fetch(`${API_BASE_URL}/zhadiger/api/stt-audio`, {
      method: 'POST',
      headers,
      body: base64Audio,
    })
      .then((response) => response.text())
      .then((data) => {
        setTranscription(data);
        message.success('Transcription received');
      })
      .catch((error) => {
        console.error('Error sending audio to server:', error);
        message.error('Error sending audio to server');
      });
  };
  return (
    <div className="flex flex-row items-start p-6 space-x-6">
      <div className="flex flex-col items-center space-y-6 flex-grow">
        <div className="flex-shrink-0 self-start">
          <HomeButton />
        </div>
        <Title level={2} className="text-center">
          {t('speech_to_text')}
        </Title>
        <Card className="w-full max-w-md">
          <Space direction="vertical" className="w-full">
            <Button
              type="primary"
              icon={recording ? <StopOutlined /> : <AudioOutlined />}
              danger={recording}
              onClick={recording ? stopRecording : startRecording}
              block
            >
              {recording ? t('stop_recording') : t('start_recording')}
            </Button>

            {audioUrl && (
              <div>
                <Title level={4}>{t('recorded_audio')}:</Title>
                <audio src={audioUrl} controls className="w-full" />
              </div>
            )}

            <div>
              <Title level={4}>{t('transcription')}:</Title>
              <textarea
                readOnly
                value={transcription}
                placeholder={
                  t('transcription_desc') || 'Transcription will appear here...'
                }
                rows={5}
                className="w-full p-3 border border-gray-300 rounded-md"
              />
            </div>
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default SpeechToTextPage;
